import { useEffect, useReducer, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, getFeedbackDraft, getFeedbackPublication, getFeedbackWorkspace, isCurrentFeedbackDraftScope } from '@/lib/api';
import { useComposerDraftContext, draftContextKey, isCurrentDraftContext } from '@/features/coach-review/composerDrafts';
import { continueFeedbackDraft, continueFeedbackPublication, continueFeedbackWorkspace,
  loadFeedbackContinuation, rememberFeedbackWorkspace, restoreFeedbackContinuation, isFeedbackProseWithinLimit } from '@/lib/feedbackAuthoringContinuation';
import { feedbackDraftKey, feedbackPublicationKey, feedbackWorkspaceKey, queryClient } from '@/lib/queryClient';
import type { FeedbackDraftResponse, FeedbackDraftScope, FeedbackWorkspaceResponse } from '@/lib/types';
import { editableDraftContent, feedbackContentSchema, emptyFeedbackContent, emptyPendingReference,
  type FeedbackContent, type FeedbackPendingReference } from '../feedbackContent';

type WritingInput = { mode: 'content'; content: FeedbackContent } | { mode: 'prose'; message: string; next_step: string };

type Options = { scope: FeedbackDraftScope; draftId?: string; workspaceId?: string; entryId?: string;
  entryPlayerId?: string; enabled: boolean };

/** React coordination only. Shared controllers own immutable capture, storage,
 * attempt ownership, uncertainty and original-operation replay. */
export function useFeedbackAuthoring(options: Options) {
  const { scope, enabled } = options;
  const liveContext = useComposerDraftContext(), context = useRef(liveContext);
  if (!context.current && liveContext) context.current = liveContext;
  const [entryId] = useState(() => options.entryId ?? crypto.randomUUID());
  const [, render] = useReducer(value => value + 1, 0);
  const [redacted, setRedacted] = useState(false);
  const [restored, setRestored] = useState(false);
  const [ready, setReady] = useState(false), [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false), [refreshing, setRefreshing] = useState(false);
  const [draftSource, setDraftSource] = useState<FeedbackDraftResponse | null>(null);
  const [workspace, setWorkspace] = useState<FeedbackWorkspaceResponse | null>(null);
  const [writingSaved, setWritingSaved] = useState(false);
  const [lookup, setLookup] = useState({ draftId: options.draftId, workspaceId: options.workspaceId });
  const alive = useRef(true), abort = useRef(new AbortController()), initialized = useRef(false);
  const targetReady = useRef(false);
  const target = useRef<{ id: string; version: number } | null>(null);
  const workspaceTarget = useRef<FeedbackWorkspaceResponse | null>(null);
  const workspaceRead = useRef<FeedbackWorkspaceResponse | null>(null);
  const draftCommand = useRef<ReturnType<typeof continueFeedbackDraft> | null>(null);
  const workspaceCommand = useRef<ReturnType<typeof continueFeedbackWorkspace> | null>(null);
  const publication = useRef<ReturnType<typeof continueFeedbackPublication> | null>(null);
  const publicationOperation = useRef<string | null>(null);
  const restoredReference = useRef<FeedbackPendingReference | null>(null);
  const restoredProse = useRef<Pick<FeedbackContent, 'message' | 'next_step'> | null>(null);
  const restoredContent = useRef<FeedbackContent | null>(null);
  const activeNow = useRef({ enabled, liveContext, denied }); activeNow.current = { enabled, liveContext, denied };
  const current = () => alive.current && activeNow.current.enabled && !activeNow.current.denied
    && !!context.current && draftContextKey(context.current) === draftContextKey(activeNow.current.liveContext)
    && context.current.actorId === scope.actorId && context.current.clubId === scope.clubId
    && context.current.teamId === scope.teamId && context.current.seasonId === scope.seasonId
    && isCurrentDraftContext(context.current) && isCurrentFeedbackDraftScope(scope);
  useEffect(() => {
    alive.current = true; abort.current = new AbortController();
    return () => { alive.current = false; abort.current.abort(); };
  }, []);
  useEffect(() => {
    if (initialized.current || !current() || !context.current) return;
    initialized.current = true;
    try {
      const row = loadFeedbackContinuation(context.current, entryId);
      if (row && (options.draftId && row.feedback_id && options.draftId !== row.feedback_id
        || options.workspaceId && row.workspace_id && options.workspaceId !== row.workspace_id)) throw new Error('Continuation target changed');
      const command = row?.pending?.command;
      const capturedDraft = command && ('feedbackId' in command ? command.feedbackId
        : 'feedback_id' in command.body ? command.body.feedback_id : null);
      const capturedWorkspace = command && ('workspaceId' in command ? command.workspaceId
        : 'workspace' in command.body ? command.body.workspace?.workspace_id : null);
      if (options.draftId && capturedDraft && options.draftId !== capturedDraft
        || options.workspaceId && capturedWorkspace && options.workspaceId !== capturedWorkspace) throw new Error('Continuation target changed');
      const restored = restoreFeedbackContinuation(context.current, entryId);
      setRestored(!!restored);
      if (restored?.kind === 'publication') {
        publication.current = restored.controller;
        const intent = restored.controller.getPublicationIntent()!;
        publicationOperation.current = intent.body.operation_id;
        restoredContent.current = { ...emptyFeedbackContent(), ...intent.body.content };
      } else if (restored) {
        if (restored.kind === 'draft') draftCommand.current = restored.controller;
        else workspaceCommand.current = restored.controller;
        const intent = restored.controller.getIntent();
        if (intent?.kind === 'workspace' && intent.command.action === 'save') {
          const body = intent.command.body;
          restoredReference.current = { pending_reference_input: body.pending_reference_input ?? '',
            pending_reference_reason: body.pending_reference_reason ?? '', pending_reference_error: body.pending_reference_error ?? '' };
          if (body.mode === 'prose') restoredProse.current = { message: body.message ?? '', next_step: body.next_step ?? '' };
        }
        if (intent && 'content' in intent.command.body) restoredContent.current = { ...emptyFeedbackContent(), ...intent.command.body.content };
      }
      setLookup({ draftId: options.draftId ?? row?.feedback_id ?? undefined, workspaceId: options.workspaceId ?? row?.workspace_id ?? undefined });
      setReady(true);
    } catch { setError('Kept work could not be opened. Close and try again; nothing has been sent.'); }
  });
  const pending = !!(draftCommand.current || workspaceCommand.current || publication.current);
  const workspaceQuery = useQuery({ queryKey: feedbackWorkspaceKey(scope, lookup.workspaceId ?? 'none'),
    queryFn: async ({ signal }) => {
      const value = await getFeedbackWorkspace(lookup.workspaceId!, { scope, signal });
      if (current()) {
        if (options.draftId && value.feedback_id !== options.draftId) throw new Error('Workspace target changed');
        if (!workspaceTarget.current) workspaceTarget.current = value;
        workspaceRead.current = value; setWorkspace(value);
        if (context.current) try { rememberFeedbackWorkspace(context.current, entryId, value.workspace_id); }
        catch { setError('This draft opened, but its resume link could not be kept in this tab.'); }
      } return value;
    }, enabled: ready && enabled && !denied && !pending && !!lookup.workspaceId, staleTime: 0, retry: false, refetchOnWindowFocus: false });
  const query = useQuery({ queryKey: feedbackDraftKey(scope, lookup.draftId ?? 'new'),
    queryFn: async ({ signal }) => { const value = await getFeedbackDraft(lookup.draftId!, { scope, signal });
      if (current()) { setDraftSource(value); } return value; },
    enabled: ready && enabled && !denied && !pending && !lookup.workspaceId && !!lookup.draftId,
    retry: false, refetchOnWindowFocus: false, staleTime: 0 });
  const initial = workspace ?? draftSource;
  if (!targetReady.current && initial) {
    target.current = initial.feedback_id ? { id: initial.feedback_id,
      version: initial.schema_version === 'feedback-workspace.v1' ? initial.base_version : initial.version } : null;
    targetReady.current = true;
  }
  // Current parent metadata selects new actions only; it never changes the
  // captured edit target/CAS or an original pending command.
  function canSaveDraftNow() {
    const view = workspaceRead.current;
    return view ? view.current_feedback_publication_state === 'draft'
      || view.feedback_id === null && !target.current && view.current_feedback_publication_state === null
      : !target.current || !!draftSource;
  }
  const canReviewNow = () => canSaveDraftNow() || workspaceRead.current?.current_feedback_publication_state === 'published';
  const parentState = workspaceRead.current?.current_feedback_publication_state;
  const canSaveDraft = canSaveDraftNow(), canReview = canReviewNow();
  const discardTarget: 'draft' | 'workspace' = workspaceCommand.current?.getIntent()?.command.action === 'discard' ? 'workspace'
    : draftCommand.current?.getIntent()?.command.action === 'discard' ? 'draft'
      : workspaceTarget.current && (!target.current || !canSaveDraft) ? 'workspace' : 'draft';
  function continuationSettled() {
    try {
      if (context.current && !loadFeedbackContinuation(context.current, entryId)?.pending) return true;
    } catch { /* validated acceptance stays real; keep the original controller */ }
    setError('The operation was accepted. Its original recovery is still kept; check it again before editing.');
    return false;
  }
  function prepare<T>(factory: () => T): T | null {
    try { return factory(); }
    catch { setError('This writing could not be prepared. Check its fields and try again. Nothing new was sent.'); return null; }
  }
  function locked() {
    return [draftCommand.current?.getState(), workspaceCommand.current?.getState(), publication.current?.getState()]
      .some(state => state && state.status !== 'rejected' && state.status !== 'reviewed' && state.status !== 'review-failed');
  }
  function failed(cause: unknown) {
    if (!current()) return;
    const code = cause instanceof ApiError && cause.detail && typeof cause.detail === 'object' && 'code' in cause.detail ? cause.detail.code : null;
    if (['feedback_unavailable', 'workspace_unavailable'].includes(String(code))) setRedacted(true);
    if (cause instanceof ApiError && (cause.status === 401 || ['feedback_access_required', 'feedback_creator_required',
      'feedback_scope_invalid', 'workspace_access_required', 'workspace_scope_invalid'].includes(String(code)))) { setDenied(true); return; }
    const accepted = [draftCommand.current?.getState(), workspaceCommand.current?.getState(), publication.current?.getState()].some(state => state?.status === 'accepted');
    if (accepted) { setError('The operation was accepted. Its current view could not be refreshed; check the original operation to retry the read.'); return; }
    setError(String(code).includes('version_conflict') || String(code).includes('origin_conflict')
      ? 'This Feedback changed. Your writing is still here. Open the current saved version separately before trying again.'
      : cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && !locked()
        ? 'This action could not be completed. Your writing is still here. Check it and try again.'
        : 'The result could not be confirmed. Your original operation and writing are kept.');
  }
  function releaseRejected() {
    try {
      if (publication.current?.getState().status === 'review-failed') publication.current = null;
      if (draftCommand.current?.getState().status === 'rejected') { draftCommand.current.releaseRejected(); draftCommand.current = null; }
      if (workspaceCommand.current?.getState().status === 'rejected') { workspaceCommand.current.releaseRejected(); workspaceCommand.current = null; }
      if (publication.current?.getState().status === 'rejected') { publication.current.releaseRejected(); publication.current = null; }
      return true;
    } catch { setError('The original operation is still kept. Retry it before changing the action.'); return false; }
  }
  async function freshWorkspace(id: string, expectedVersion?: number) {
    const fresh = await queryClient.fetchQuery({ queryKey: feedbackWorkspaceKey(scope, id),
      queryFn: ({ signal }) => getFeedbackWorkspace(id, { scope, signal }), staleTime: 0 });
    if (!current()) return null;
    if (expectedVersion !== undefined && fresh.version !== expectedVersion) {
      setError('Draft changes were saved, but have changed since. Open the draft separately to continue.'); return null;
    }
    workspaceTarget.current = fresh; workspaceRead.current = fresh; setWorkspace(fresh);
    if (context.current) try { rememberFeedbackWorkspace(context.current, entryId, fresh.workspace_id); }
    catch { setError('This draft opened, but its original recovery or resume link still needs attention.'); }
    targetReady.current = true;
    target.current = fresh.feedback_id ? { id: fresh.feedback_id, version: fresh.base_version } : null; return fresh;
  }
  async function reconcileWorkspace() {
    if (!current() || !workspaceCommand.current || workspaceCommand.current.getState().status === 'sending') return null;
    try {
      setError(null); const promise = workspaceCommand.current.send(abort.current.signal); render(); const result = await promise;
      if (!current()) return null;
      if (result.schema_version === 'feedback-workspace-discard-result.v1') return result;
      if (result.schema_version !== 'feedback-workspace-result.v1') return null;
      setRefreshing(true); const fresh = await freshWorkspace(result.workspace_id, result.version);
      if (!fresh || fresh.version !== result.version) {
        setError('Draft changes were saved, but have changed since. Open the draft separately to continue.'); return null;
      }
      if (!continuationSettled()) return null;
      workspaceCommand.current = null; restoredContent.current = null; setRestored(false);
      setWritingSaved(fresh.has_unsaved_feedback); return fresh;
    } catch (cause) { failed(cause); return null; }
    finally { if (current()) { setRefreshing(false); render(); } }
  }
  async function startWorkspace() {
    if (!current() || !context.current || locked() || workspaceTarget.current || !releaseRejected()) return null;
    workspaceCommand.current = prepare(() => continueFeedbackWorkspace(context.current!, entryId, { action: 'create', body: {
      schema_version: 'feedback-workspace-create.v1', operation_id: crypto.randomUUID(), feedback_id: target.current?.id ?? null,
      expected_feedback_version: target.current?.version ?? 0, entry_player_id: options.entryPlayerId ?? null,
    } })); return reconcileWorkspace();
  }
  async function saveWriting(value: WritingInput, reference: FeedbackPendingReference = emptyPendingReference) {
    if (publication.current?.getState().status === 'review-failed') publication.current = null;
    if (!current() || !context.current || refreshing || publication.current || draftCommand.current) return null;
    if (!workspaceCommand.current) {
      const prose = value.mode === 'content' ? value.content : value;
      if (!isFeedbackProseWithinLimit('message', prose.message) || !isFeedbackProseWithinLimit('next_step', prose.next_step)
        || value.mode === 'content' && !feedbackContentSchema.safeParse(value.content).success) {
        setError('Check the writing and its fields before saving. Your changes are still here.'); return null;
      }
      if ([...reference.pending_reference_input].length > 2048 || [...reference.pending_reference_reason].length > 200 || [...reference.pending_reference_error].length > 1000) {
        setError('Check the video link and reason lengths before keeping this writing.'); return null;
      }
      const source = workspaceTarget.current; if (!source || !releaseRejected()) return null;
      const base = { schema_version: 'feedback-workspace-save.v1' as const, operation_id: crypto.randomUUID(), expected_version: source.version, ...reference };
      workspaceCommand.current = prepare(() => continueFeedbackWorkspace(context.current!, entryId, { action: 'save', workspaceId: source.workspace_id,
        body: { ...base, ...value } }));
    } return reconcileWorkspace();
  }
  function captureWorkspace() {
    const value = workspaceTarget.current;
    return value ? { workspace_id: value.workspace_id, expected_version: value.version } : undefined;
  }
  async function clearPendingReference(content: FeedbackContent) {
    const source = workspaceTarget.current;
    if (!source || ![source.pending_reference_input, source.pending_reference_reason, source.pending_reference_error].some(Boolean)) return true;
    return !!await saveWriting({ mode: 'content', content }, emptyPendingReference);
  }
  async function save(content: FeedbackContent) {
    if (publication.current?.getState().status === 'review-failed') publication.current = null;
    if (!current() || !context.current || refreshing || publication.current || workspaceCommand.current
      || draftCommand.current?.getState().status === 'sending' || draftCommand.current?.getIntent()?.command.action === 'discard') return null;
    setError(null);
    if (!draftCommand.current) {
      if (!canSaveDraftNow()) { setError('The Feedback state changed. Save your draft changes separately or reopen the current Feedback.'); return null; }
      if (!await clearPendingReference(content) || !current()) return null;
      if (!canSaveDraftNow()) { setError('The Feedback state changed. Save your draft changes separately or reopen the current Feedback.'); return null; }
      const body = { schema_version: 'feedback.v1' as const, operation_id: crypto.randomUUID(),
        expected_version: target.current?.version ?? 0, content, workspace: captureWorkspace() };
      draftCommand.current = continueFeedbackDraft(context.current, entryId, target.current
        ? { action: 'save', feedbackId: target.current.id, body } : { action: 'create', body });
    }
    try {
      const promise = draftCommand.current.send(abort.current.signal); render(); const result = await promise;
      if (!current() || result.schema_version !== 'feedback-draft-result.v1') return null;
      setRefreshing(true);
      const fresh = await queryClient.fetchQuery({ queryKey: feedbackDraftKey(scope, result.feedback_id),
        queryFn: ({ signal }) => getFeedbackDraft(result.feedback_id, { scope, signal }), staleTime: 0 });
      if (!current()) return null;
      const normalized = editableDraftContent(fresh);
      if (fresh.version !== result.version || fresh.revision_id !== result.revision_id || !normalized) {
        setError('The save was accepted, but the saved draft has changed. Your writing is still here. Open the draft separately to continue.'); return null;
      }
      const intent = draftCommand.current.getIntent();
      const submitted = intent && 'workspace' in intent.command.body ? intent.command.body.workspace : undefined;
      if (submitted) {
        const kept = await freshWorkspace(submitted.workspace_id);
        if (!kept) return null;
        if (kept.has_unsaved_feedback || !kept.accepted_origin_available || kept.base_version !== fresh.version) {
          setError('The draft was saved. Newer draft changes are still kept; open that draft separately before continuing.'); return null;
        }
      }
      if (!current()) return null;
      if (!continuationSettled()) return null;
      targetReady.current = true; target.current = { id: fresh.feedback_id, version: fresh.version }; setDraftSource(fresh);
      draftCommand.current = null; restoredContent.current = null; setRestored(false); setWritingSaved(false);
      return { content: normalized, draft: fresh, receipt: result };
    } catch (cause) { failed(cause); return null; }
    finally { if (current()) { setRefreshing(false); render(); } }
  }
  async function review(content: FeedbackContent) {
    if (!current() || !context.current || refreshing || locked() || !releaseRejected()) return;
    if (!canReviewNow()) { setError('The current Feedback state is unavailable. Keep draft changes and reopen it before reviewing.'); return; }
    if (!await clearPendingReference(content) || !current()) return;
    if (!canReviewNow()) { setError('The current Feedback state is unavailable. Keep draft changes and reopen it before reviewing.'); return; }
    setError(null);
    const body = { schema_version: 'feedback-review.v1' as const, expected_version: target.current?.version ?? 0, content, workspace: captureWorkspace() };
    publication.current = continueFeedbackPublication(context.current, entryId, target.current
      ? { action: 'existing', feedbackId: target.current.id, body } : { action: 'new', body });
    publicationOperation.current = crypto.randomUUID();
    try { const promise = publication.current.review(abort.current.signal); render(); await promise; }
    catch (cause) { failed(cause); }
    finally { if (current()) render(); }
  }
  async function publish() {
    if (!current() || draftCommand.current || workspaceCommand.current || !publication.current || !publicationOperation.current) return null;
    setError(null);
    try {
      const promise = publication.current.publish(publicationOperation.current, abort.current.signal); render(); const result = await promise;
      if (!current()) return null;
      setRefreshing(true);
      await queryClient.fetchQuery({ queryKey: feedbackPublicationKey(scope, result.feedback_id),
        queryFn: ({ signal }) => getFeedbackPublication(result.feedback_id, { scope, signal }), staleTime: 0 });
      const submitted = publication.current.getPublicationIntent()?.body.workspace;
      if (submitted && !await freshWorkspace(submitted.workspace_id)) return null;
      return current() ? result : null;
    } catch (cause) { failed(cause); return null; }
    finally { if (current()) { setRefreshing(false); render(); } }
  }
  async function discard() {
    if (publication.current?.getState().status === 'review-failed') publication.current = null;
    if (!current() || !context.current || refreshing || publication.current) return false;
    if (workspaceCommand.current) return (await reconcileWorkspace())?.schema_version === 'feedback-workspace-discard-result.v1';
    if (draftCommand.current && draftCommand.current.getIntent()?.command.action !== 'discard') {
      if (!releaseRejected() || draftCommand.current) return false;
    }
    if ((!target.current || !canSaveDraftNow()) && draftCommand.current?.getIntent()?.command.action !== 'discard') {
      const source = workspaceTarget.current;
      if (!source) return !target.current;
      workspaceCommand.current = continueFeedbackWorkspace(context.current, entryId, { action: 'discard', workspaceId: source.workspace_id,
        body: { schema_version: 'feedback-workspace-discard.v1', operation_id: crypto.randomUUID(), expected_version: source.version } });
      return (await reconcileWorkspace())?.schema_version === 'feedback-workspace-discard-result.v1';
    }
    if (!draftCommand.current) draftCommand.current = continueFeedbackDraft(context.current, entryId, { action: 'discard', feedbackId: target.current!.id,
      body: { schema_version: 'feedback-version.v1', operation_id: crypto.randomUUID(), expected_version: target.current!.version } });
    try { const promise = draftCommand.current.send(abort.current.signal); render(); const result = await promise;
      return current() && result.schema_version === 'feedback-draft-discard-result.v1';
    } catch (cause) { failed(cause); return false; }
    finally { if (current()) render(); }
  }
  const draftState = draftCommand.current?.getState(), workspaceState = workspaceCommand.current?.getState(), publicationState = publication.current?.getState();
  const states = [draftState, workspaceState, publicationState];
  const busy = refreshing || states.some(state => state && ['sending', 'reviewing', 'publishing'].includes(state.status));
  const uncertain = states.some(state => state && ['ready', 'unknown', 'retired', 'accepted'].includes(state.status));
  const activeQuery = lookup.workspaceId ? workspaceQuery : query;
  const readUnavailable = !pending && activeQuery.error instanceof ApiError && [401, 403, 404].includes(activeQuery.error.status);
  // Display the origin belonging to this captured edit, not a newer workspace
  // GET. Only our existing acknowledged hydration advances workspaceTarget.
  // Current parent metadata is contextual and never rebases that origin/CAS.
  const workspaceStatus = workspaceTarget.current ? {
    baseVersion: workspaceTarget.current.base_version,
    originAvailable: workspaceTarget.current.accepted_origin_available,
    hasUnsavedFeedback: workspaceTarget.current.has_unsaved_feedback,
    currentVersion: workspaceRead.current?.current_feedback_version,
  } : null;
  return { query: activeQuery, initial, ready, restored, redacted, accepted: states.some(state => state?.status === 'accepted'), entryId, workspace, writingSaved, error, denied, readUnavailable, busy, uncertain,
    workspaceStatus,
    draftState, workspaceState, publicationState, restoredReference: restoredReference.current, restoredProse: restoredProse.current, restoredContent: restoredContent.current,
    needsRead: !!(lookup.workspaceId || lookup.draftId) && !pending, hasPending: pending,
    hasFeedback: !!target.current, hasSavedDraft: !!target.current && canSaveDraft,
    canSaveDraft, canReview, isPublishedWorkspace: parentState === 'published', discardTarget,
    save, review, publish, discard, startWorkspace, saveWriting, reconcileWorkspace,
    pendingAction: draftCommand.current?.getIntent()?.command.action,
    pendingWorkspaceAction: workspaceCommand.current?.getIntent()?.command.action,
    backToWriting: () => { if (!busy && !uncertain && releaseRejected()) { publication.current = null; setError(null); render(); } },
    changed: () => { if (!busy && !uncertain && releaseRejected()) { publication.current = null; setWritingSaved(false); setError(null); render(); } },
  };
}
